import { useMemo, useState } from "react";
import { useFetch } from "@/hooks/useFetch";
import { useBase } from "@/context/BaseContext";
import { usePayrollSettings } from "@/context/PayrollSettingsContext";
import { useMinsFor } from "@/lib/useBaseMins";
import { computeAutoBonus } from "@/lib/bonusConfig";
import { useContractTypes } from "@/lib/useContractTypes";
import { Loading, ErrorState, Empty } from "@/components/DataState";
import { brl, num, km } from "@/lib/format";
import { Trophy, Medal } from "lucide-react";

const MONTHS = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const VEHICLE = { ciclista: "Ciclista", motociclista: "Motoboy" };
const LEVEL_CLS = {
  Bronze: "bg-orange-900/40 text-orange-300", Prata: "bg-slate-500/20 text-slate-200", Ouro: "bg-amber-500/20 text-amber-300",
  Platina: "bg-cyan-500/15 text-cyan-200", Diamante: "bg-sky-500/20 text-sky-300", Mestre: "bg-fuchsia-500/20 text-fuchsia-300",
};
const PODIUM = ["text-amber-300", "text-slate-300", "text-orange-400"];
const hours = (s) => `${Math.floor((s || 0) / 3600)}h${String(Math.floor(((s || 0) % 3600) / 60)).padStart(2, "0")}`;

const LevelBadge = ({ name, testId }) => (name
  ? <span className={`rounded px-2 py-0.5 text-xs font-semibold ${LEVEL_CLS[name] || "bg-slate-800 text-slate-300"}`} data-testid={testId}>{name}</span>
  : <span className="text-xs text-slate-600" data-testid={testId}>—</span>);

function useMonthOptions() {
  return useMemo(() => {
    const now = new Date();
    return Array.from({ length: 12 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      return { y: d.getFullYear(), m: d.getMonth() + 1, label: `${MONTHS[d.getMonth()]} de ${d.getFullYear()}` };
    });
  }, []);
}

export default function Ranking() {
  const months = useMonthOptions();
  const [mIdx, setMIdx] = useState(0);
  const [period, setPeriod] = useState("mensal");
  const [vehicle, setVehicle] = useState("all");
  const { y: year, m: month } = months[mIdx];
  const r1 = useFetch("/admin/reports", { year, month, quinzena: 1 });
  const r2 = useFetch("/admin/reports", { year, month, quinzena: 2 });
  const cfg = useFetch("/config");
  const tiersData = useFetch("/admin/bonus-tiers");
  const collabs = useFetch("/admin/collaborators");
  const { selectedBaseId } = useBase();
  const { settings } = usePayrollSettings();
  const minsFor = useMinsFor();
  const contractOf = useContractTypes();

  const rows = useMemo(() => {
    const mins = { ...(cfg.data || {}), mestre_min: cfg.data?.mestre_min ?? settings.mestre_min };
    const tiers = tiersData.data?.tiers || [];
    const inBaseIds = new Set((collabs.data?.collaborators || []).map((c) => c.id));
    const map = {};
    const add = (list, q) => (list || []).forEach((r) => {
      const ct = contractOf(r.id);
      const e = map[r.id] || (map[r.id] = { id: r.id, name: r.name, vehicle: r.vehicle_type, deliveries: 0, km: 0, work: 0, routes: 0, bonus: 0, levels: {}, pj: ct === "pj" });
      e.deliveries += r.deliveries || 0; e.km += r.km || 0; e.work += r.work_seconds || 0; e.routes += r.routes_count || 0;
      const b = computeAutoBonus(r.deliveries || 0, minsFor(r.id, mins), tiers, ct);
      e.bonus += b.value;
      e.levels[q] = ct === "pj" ? null : r.level?.name || b.level;
    });
    if (period !== 2) add(r1.data?.rows, 1);
    if (period !== 1) add(r2.data?.rows, 2);
    return Object.values(map)
      .filter((e) => !selectedBaseId || inBaseIds.has(e.id))
      .filter((e) => vehicle === "all" || e.vehicle === vehicle)
      .sort((a, b) => b.deliveries - a.deliveries || b.km - a.km);
  }, [r1.data, r2.data, cfg.data, tiersData.data, collabs.data, settings.mestre_min, minsFor, period, vehicle, selectedBaseId, contractOf]);

  const loading = r1.loading || r2.loading || (selectedBaseId && collabs.loading) || !contractOf.ready;
  const error = r1.error || r2.error;
  const btn = (active) => `px-3 py-1.5 text-sm font-medium transition-colors ${active ? "bg-orange-500 text-white" : "bg-slate-900 text-slate-300 hover:bg-slate-800"}`;

  return (
    <div className="space-y-6" data-testid="ranking-page">
      <p className="flex items-center gap-2 text-sm text-slate-500"><Trophy className="h-4 w-4 text-amber-400" /> Entregadores ordenados por entregas no período. Categoria e bônus seguem as regras de Benefícios.</p>
      <div className="flex flex-wrap items-center gap-3">
        <select value={mIdx} onChange={(e) => setMIdx(Number(e.target.value))} data-testid="ranking-month"
          className="h-9 rounded-md border border-slate-700 bg-slate-950 px-2 text-sm text-slate-100">
          {months.map((o, i) => <option key={o.label} value={i}>{o.label}</option>)}
        </select>
        <div className="flex overflow-hidden rounded-lg border border-slate-700">
          {[{ v: "mensal", l: "Mensal" }, { v: 1, l: "1ª Quinzena" }, { v: 2, l: "2ª Quinzena" }].map((p) => (
            <button key={p.v} onClick={() => setPeriod(p.v)} className={btn(period === p.v)} data-testid={`ranking-period-${p.v}`}>{p.l}</button>
          ))}
        </div>
        <div className="flex overflow-hidden rounded-lg border border-slate-700">
          {[{ v: "all", l: "Todos" }, { v: "ciclista", l: "Ciclistas" }, { v: "motociclista", l: "Motoboys" }].map((p) => (
            <button key={p.v} onClick={() => setVehicle(p.v)} className={btn(vehicle === p.v)} data-testid={`ranking-vehicle-${p.v}`}>{p.l}</button>
          ))}
        </div>
      </div>
      {loading ? <Loading /> : error ? <ErrorState error={error} onRetry={() => { r1.refetch(); r2.refetch(); }} /> : rows.length === 0 ? <Empty label="Nenhum entregador com dados neste período" /> : (
        <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900">
          <table className="w-full min-w-[820px] text-sm" data-testid="ranking-table">
            <thead>
              <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 font-semibold">#</th>
                <th className="px-4 py-3 font-semibold">Entregador</th>
                <th className="px-4 py-3 text-right font-semibold">Entregas</th>
                <th className="px-4 py-3 text-right font-semibold">KM</th>
                <th className="px-4 py-3 text-right font-semibold">Rotas</th>
                <th className="px-4 py-3 text-right font-semibold">Horas</th>
                <th className="px-4 py-3 font-semibold">Categoria</th>
                <th className="px-4 py-3 text-right font-semibold">Bônus produtividade</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {rows.map((r, i) => (
                <tr key={r.id} className={`hover:bg-slate-800/40 ${i < 3 ? "bg-amber-500/[0.03]" : ""}`} data-testid={`ranking-row-${r.id}`}>
                  <td className="px-4 py-3 font-mono text-slate-400" data-testid={`ranking-pos-${r.id}`}>
                    {i < 3 ? <Medal className={`h-5 w-5 ${PODIUM[i]}`} /> : `${i + 1}º`}
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-100">{r.name}</p>
                    <p className="text-xs text-slate-500">{VEHICLE[r.vehicle] || r.vehicle || "—"}</p>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-100" data-testid={`ranking-deliveries-${r.id}`}>{num(r.deliveries)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-300">{km(r.km)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-300">{num(r.routes)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-300">{hours(r.work)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-1.5">
                      {r.pj ? <span className="text-xs text-slate-500" data-testid={`ranking-level-pj-${r.id}`}>PJ · sem produtividade</span> : period === "mensal" ? (
                        <>
                          <span className="text-[10px] text-slate-500">1ª</span><LevelBadge name={r.levels[1]} testId={`ranking-level1-${r.id}`} />
                          <span className="text-[10px] text-slate-500">2ª</span><LevelBadge name={r.levels[2]} testId={`ranking-level2-${r.id}`} />
                        </>
                      ) : <LevelBadge name={r.levels[period]} testId={`ranking-level-${r.id}`} />}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-300" data-testid={`ranking-bonus-${r.id}`}>{r.pj ? <span className="text-slate-600">—</span> : brl(r.bonus)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
